/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import classnames from 'classnames';

import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { IVinhusetNavigationMenuData } from './vinhuset-navigation-menu.data';
import { IVinhusetNavigationMenuProps, menuItemsSource } from './vinhuset-navigation-menu.props.autogenerated';

export interface IVinhusetNavigationMenuViewProps extends IVinhusetNavigationMenuProps<IVinhusetNavigationMenuData> {
    navigationMenu: IModuleProps;
    baseClassName: string;
    productMenuItem: IMenuItem;
    menuItems: IMenuItem[];
}

export interface IMenuItem {
    title: string;
    url?: string;
    children?: IMenuItem[];
    showUnauthorized: boolean;
}

/**
 *
 * VinhusetNavigationMenu component
 * @extends {React.PureComponent<IVinhusetNavigationMenuProps<IVinhusetNavigationMenuData>>}
 */
class VinhusetNavigationMenu extends React.PureComponent<IVinhusetNavigationMenuProps<IVinhusetNavigationMenuData>> {
    private readonly moduleClassName: string = 'vinhuset-navigation-menu';

    public render(): JSX.Element | null {
        const {
            config: {
                cmsNavItems,
                showProductsMenuItemOnlyAuthorized,
                showAllProductsLink,
                showAllProductsLabel
            },
            context,
            data: { categories },
            resources: { productsMenuItemLabel }
        } = this.props;

        const isAuthenticated = context.request.user.isAuthenticated;
        const menuItems: IMenuItem[] = [];
        let productMenuItemChildren: IMenuItem[] | undefined;

        // TODO: Figure out how to place home menu item first

        if (this.props.config.menuItemsSource === menuItemsSource.static) {
            cmsNavItems?.filter(x => !x.showOnlyAuthorized || isAuthenticated).forEach(navItem => {
                if (!navItem.linkText || !navItem.linkUrl) {
                    return;
                }

                menuItems.push({
                    title: navItem.linkText,
                    url: navItem.linkUrl.destinationUrl,
                    showUnauthorized: !navItem.showOnlyAuthorized
                });
            });

            productMenuItemChildren = categories.result
                ?.sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
                ?.map(category => ({
                    title: category.Name,
                    url: category.Url
                }) as IMenuItem);

            if (showAllProductsLink && showAllProductsLink.destinationUrl) {
                productMenuItemChildren?.unshift(
                    {
                        title: showAllProductsLabel || 'All products',
                        url: showAllProductsLink.destinationUrl,
                        showUnauthorized: false
                    },
                    {
                        title: '<SEPARATOR>',
                        showUnauthorized: false
                    }
                );
            }
        } else {
            if (!isAuthenticated) {
                return null;
            }

            categories.result
                ?.sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
                ?.forEach(category => {
                    menuItems.push({
                        title: category.Name || 'Missing category name',
                        url: category.Url,
                        showUnauthorized: false
                    });
                });

            if (showAllProductsLink && showAllProductsLink.destinationUrl) {
                menuItems?.unshift(
                    {
                        title: showAllProductsLabel || 'All products',
                        url: showAllProductsLink.destinationUrl,
                        showUnauthorized: false
                    }
                );
            }
        }

        const viewProps: IVinhusetNavigationMenuViewProps = {
            ...this.props,
            navigationMenu: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, this.props.config.className)
            },
            baseClassName: this.moduleClassName,
            productMenuItem: {
                title: productsMenuItemLabel,
                children: productMenuItemChildren,
                showUnauthorized: !showProductsMenuItemOnlyAuthorized
            },
            menuItems
        };

        return this.props.renderView(viewProps);
    }
}

export default VinhusetNavigationMenu;
